/**
 * Constrói o grafo de dados estruturados (schema.org) da página.
 *
 * Cap. IV, I da OS 0007/2026 — "adoção de formatos estruturados e
 * interoperáveis". É o que permite ao buscador entender que uma página é uma
 * notícia, com data, autor e assunto, em vez de texto solto.
 *
 * Os construtores são funções puras SEM `Date.now()`, `Math.random()`,
 * `window` ou formatação de data dependente de fuso: a string precisa sair
 * idêntica no servidor e no cliente, senão o Helmet reescreve o `<head>` na
 * hidratação. As datas vão cruas do backend, em ISO.
 */

import { flattenToAppURL, toPublicURL } from '@plone/volto/helpers/Url/Url';

import { ARTICLE_PORTAL_TYPES } from './constants';
import { OgImage } from './ogImage';
import { singleLine, titleOf } from './text';

/** Limite do `headline` recomendado pelo Google para NewsArticle. */
const HEADLINE_MAX = 110;

export interface BreadcrumbItem {
  title: string;
  url: string;
}

export interface StructuredDataInput {
  content: any;
  siteName: string;
  canonicalUrl?: string;
  image?: OgImage | null;
  breadcrumbs?: BreadcrumbItem[];
  /** Rótulo do primeiro degrau da trilha, que a API não devolve. */
  homeLabel?: string;
  /** Só a página inicial descreve o site e a instituição. */
  isSiteRoot?: boolean;
  /** Origem pública, âncora dos `@id` do grafo. */
  publicUrl: string;
  /** `plone.site_logo`, quando configurado no painel do site. */
  logoUrl?: string;
}

const truncate = (text: string, max: number): string =>
  text.length <= max ? text : `${text.slice(0, max - 1).trimEnd()}…`;

const organizationId = (publicUrl: string) => `${publicUrl}/#organization`;
const websiteId = (publicUrl: string) => `${publicUrl}/#website`;

/**
 * `GovernmentOrganization` em vez de `Organization`: é o subtipo correto para
 * um órgão público e dá ao buscador um sinal a mais sobre a natureza do site.
 */
export const buildOrganization = ({
  siteName,
  publicUrl,
  logoUrl,
}: Pick<StructuredDataInput, 'siteName' | 'publicUrl' | 'logoUrl'>) => ({
  '@type': 'GovernmentOrganization',
  '@id': organizationId(publicUrl),
  name: siteName,
  alternateName: 'MPF',
  url: `${publicUrl}/`,
  ...(logoUrl ? { logo: toPublicURL(flattenToAppURL(logoUrl)) } : {}),
});

export const buildWebSite = ({
  siteName,
  publicUrl,
  inLanguage,
}: Pick<StructuredDataInput, 'siteName' | 'publicUrl'> & {
  inLanguage?: string;
}) => ({
  '@type': 'WebSite',
  '@id': websiteId(publicUrl),
  name: siteName,
  url: `${publicUrl}/`,
  ...(inLanguage ? { inLanguage } : {}),
  publisher: { '@id': organizationId(publicUrl) },
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${publicUrl}/search?SearchableText={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
});

export const buildBreadcrumbList = (
  items: BreadcrumbItem[],
  publicUrl: string,
  homeLabel = 'Início',
) => {
  if (!items?.length) return null;

  const trail = [{ title: homeLabel, url: '/' }, ...items];

  return {
    '@type': 'BreadcrumbList',
    '@id': `${publicUrl}/#breadcrumb`,
    itemListElement: trail.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: singleLine(item.title),
      item: toPublicURL(flattenToAppURL(item.url) || '/'),
    })),
  };
};

export const buildNewsArticle = ({
  content,
  canonicalUrl,
  image,
  publicUrl,
}: Pick<
  StructuredDataInput,
  'content' | 'canonicalUrl' | 'image' | 'publicUrl'
>) => {
  if (!ARTICLE_PORTAL_TYPES.includes(content?.['@type'])) return null;

  const headline = truncate(
    singleLine(content.seo_title || content.title),
    HEADLINE_MAX,
  );
  const section = singleLine(titleOf(content.tema));
  const unit = singleLine(titleOf(content.unidadeOrigem));
  const description = singleLine(
    content.seo_description || content.description,
  );

  return {
    '@type': 'NewsArticle',
    '@id': canonicalUrl ? `${canonicalUrl}#article` : undefined,
    ...(canonicalUrl
      ? { mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl } }
      : {}),
    headline,
    ...(description ? { description } : {}),
    ...(content.UID ? { identifier: content.UID } : {}),
    ...(content.language?.token ? { inLanguage: content.language.token } : {}),
    ...(content.effective || content.created
      ? { datePublished: content.effective || content.created }
      : {}),
    ...(content.modified ? { dateModified: content.modified } : {}),
    ...(image ? { image: [image.url] } : {}),
    ...(section ? { articleSection: section } : {}),
    ...(Array.isArray(content.subjects) && content.subjects.length
      ? { keywords: content.subjects }
      : {}),
    // A autoria institucional é a unidade que publicou; o MPF não credita
    // jornalista nas matérias do portal.
    author: unit
      ? { '@type': 'GovernmentOrganization', name: unit }
      : { '@id': organizationId(publicUrl) },
    publisher: { '@id': organizationId(publicUrl) },
    isPartOf: { '@id': websiteId(publicUrl) },
  };
};

export const buildStructuredData = (input: StructuredDataInput): unknown[] => {
  const { content, breadcrumbs, isSiteRoot, publicUrl, homeLabel } = input;
  if (!content) return [];

  const graph: unknown[] = [];

  // A instituição e o site são descritos uma vez, na home: repetir o mesmo
  // `@id` em toda página é ruído, e os demais nós já apontam para ele.
  if (isSiteRoot) {
    graph.push(buildOrganization(input));
    graph.push(buildWebSite({ ...input, inLanguage: content.language?.token }));
  }

  const article = buildNewsArticle(input);
  if (article) graph.push(article);

  const breadcrumb = buildBreadcrumbList(
    breadcrumbs ?? [],
    publicUrl,
    homeLabel,
  );
  if (breadcrumb) graph.push(breadcrumb);

  return graph;
};

export default buildStructuredData;
